import { useState, useCallback } from "react";

export interface SubscriptionLimitState {
  isOpen: boolean;
  limitType:
    | "projects"
    | "aiQuery"
    | "icpGeneration"
    | "adStrategy"
    | "insights"
    | "adSpend"
    | null;
  current: number;
  limit: number;
}

export interface SubscriptionError {
  invalidSubscription: boolean;
  error: string;
  limit: number;
  current: number;
}

export interface APIResponse<T = any> {
  data?: T;
  error?: string;
  invalidSubscription?: boolean;
  limit?: number;
  current?: number;
}

export const useSubscriptionLimit = (onUpgrade?: () => void) => {
  const [state, setState] = useState<SubscriptionLimitState>({
    isOpen: false,
    limitType: null,
    current: 0,
    limit: 0,
  });

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleSubscriptionError = useCallback((response: APIResponse) => {
    // Check if it's a subscription validation error
    if (
      response.invalidSubscription &&
      response.limit !== undefined &&
      response.current !== undefined
    ) {
      // Determine limit type from error message
      let limitType: SubscriptionLimitState["limitType"] = "aiQuery";
      const errorMsg = response.error?.toLowerCase() || "";

      if (errorMsg.includes("project creation")) limitType = "projects";
      if (errorMsg.includes("icp generation")) limitType = "icpGeneration";
      if (errorMsg.includes("ad strategy")) limitType = "adStrategy";
      if (errorMsg.includes("insights")) limitType = "insights";
      if (errorMsg.includes("ad spend")) limitType = "adSpend";

      setState({
        isOpen: true,
        limitType,
        current: response.current,
        limit: response.limit,
      });

      return true;
    }
    return false;
  }, []);

  const wrapRequest = useCallback(
    async <T,>(
      requestFn: () => Promise<T>
    ): Promise<{ data?: T; error?: string }> => {
      try {
        const response = await requestFn();

        // Check if response indicates a subscription limit
        if (typeof response === "object" && response !== null) {
          const apiResponse = response as APIResponse<T>;

          if (apiResponse.invalidSubscription) {
            handleSubscriptionError(apiResponse);
            return { error: apiResponse.error };
          }
        }

        return { data: response as T };
      } catch (error: any) {
        // Handle CommonService error responses
        if (error?.invalidSubscription) {
          handleSubscriptionError(error);
          return {
            error:
              error?.error ?? "Internal server error. Please contact support.",
          };
        }

        // Handle network errors from CommonService
        return error ? { error } : { error: "An unknown error occurred" };
      }
    },
    [handleSubscriptionError]
  );

  const makeRequest = useCallback(
    async <T,>(requestConfig: {
      requestFn: () => Promise<T>;
      onSuccess?: (data: T) => void;
      onError?: (error: string) => void;
    }) => {
      const { requestFn, onSuccess, onError } = requestConfig;
      const { data, error } = await wrapRequest(requestFn);

      if (error) {
        onError?.(error);
        return null;
      }

      if (data) {
        onSuccess?.(data);
        return data;
      }

      return null;
    },
    [wrapRequest]
  );

  return {
    dialogProps: {
      open: state.isOpen,
      onClose: handleClose,
      limitType: state.limitType!,
      current: state.current,
      limit: state.limit,
      onUpgrade,
    },
    makeRequest,
  };
};
